import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HardDrive } from 'lucide-react';
import { useCustomerApi } from '@/contexts/DashboardCustomerContext';
import StatusCard from './StatusCard';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';
import { formatCapacity, formatEfficiencyRatio } from '@/lib/utils/format';

interface DashboardStorageCardProps {
  onDataLoad?: (data: any) => void;
  refreshTrigger?: number;
}

export const DashboardStorageCard: React.FC<DashboardStorageCardProps> = ({ onDataLoad, refreshTrigger }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  const fetchData = async () => {
    try {
      const response = await apiClient.get('/dashboard/storage');
      setData(response.data);
      if (onDataLoad) onDataLoad(response.data);
    } catch (err: any) {
      console.error('Failed to fetch storage data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  if (loading) {
    return <SkeletonLoader variant="card" />;
  }

  // Use efficiency_ratio_with_snapshot for display (matches vendor report methodology)
  const efficiencyRatio = parseFloat(data?.summary?.efficiency_ratio_with_snapshot) || 1.0;
  const efficiencyDisplay = formatEfficiencyRatio(efficiencyRatio);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <StatusCard
        title="Storage Usage"
        value={`${data?.summary?.usage_percentage || 0}%`}
        status={
          data?.summary?.usage_percentage > 90 ? 'critical' :
          data?.summary?.usage_percentage > 75 ? 'warning' : 'healthy'
        }
        icon={<HardDrive className="w-6 h-6 text-purple-500" />}
        subtitle={
          <div className="space-y-1">
            <div>{`${formatCapacity(data?.summary?.total_used_tb)} / ${formatCapacity(data?.summary?.total_usable_tb)}`}</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[rgb(var(--text-tertiary))]">Efficiency:</span>
              <span className="text-sm font-semibold text-green-500">{efficiencyDisplay}</span>
            </div>
          </div>
        }
        trend={data?.summary?.usage_percentage > 80 ? 'up' : 'stable'}
        tooltip="Percentage of usable storage capacity currently in use. Efficiency ratio is calculated as a weighted average across all aggregates (weighted by used capacity), showing total data reduction through deduplication, compression, and thin provisioning. Includes snapshot savings. Formula: Σ(used_capacity × efficiency) / Σ(used_capacity)."
      />
    </motion.div>
  );
};

export default DashboardStorageCard;